import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { CountryCombobox } from "@/components/forms/CountryCombobox";
import { PasswordField } from "@/components/auth/PasswordField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, getErrorMessage } from "@/lib/api";


const initialForm = {
  first_name: "",
  surname: "",
  nationality: "South African",
  country: "South Africa",
  sa_id_number: "",
  passport_number: "",
  email: "",
  physical_address: "",
  phone_number: "",
  consent_privacy: false,
  consent_terms: false,
};

const SUCCESS_REDIRECT_DELAY_MS = 1200;


const getFriendlyEmailNotice = (status, supportEmail, action) => {
  if (status === "sent") {
    if (action === "setup") {
      return "Your secure setup email has been sent. Open it and create your password before trying to log in.";
    }
    return "If your details match our records, a secure email has been sent to your registered address.";
  }

  if (action === "setup") {
    return `Your request has been recorded. If the setup email does not arrive soon, contact ${supportEmail}.`;
  }

  return `Your request has been recorded. If the email does not arrive soon, contact ${supportEmail}.`;
};


const AuthPanel = ({ eyebrow, title, description, children, testId }) => (
  <Card className="border-stone-200/80 bg-white/92 shadow-[0_18px_40px_rgba(31,45,61,0.06)]" data-testid={`${testId}-card`}>
    <CardHeader className="space-y-3.5">
      <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid={`${testId}-eyebrow`}>
        {eyebrow}
      </p>
      <CardTitle className="text-4xl font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950 sm:text-5xl lg:text-[2.45rem]" data-testid={`${testId}-title`}>
        {title}
      </CardTitle>
      <p className="max-w-2xl text-sm leading-7 text-slate-600 sm:text-base sm:leading-8" data-testid={`${testId}-description`}>
        {description}
      </p>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);


const StatusMessage = ({ message, type, testId }) => {
  if (!message) return null;
  const tone = type === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700";
  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm ${tone}`} data-testid={testId}>
      {message}
    </div>
  );
};


export const ApplyPage = ({ publicConfig }) => {
  const [form, setForm] = useState(initialForm);
  const [submitState, setSubmitState] = useState({ loading: false, error: "", success: null });
  const successRef = useRef(null);

  const isSouthAfrican = form.nationality.trim().toLowerCase() === "south african";

  useEffect(() => {
    if (submitState.success) {
      successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [submitState.success, successRef]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const validate = () => {
    if (!form.first_name || !form.surname || !form.email || !form.physical_address || !form.phone_number) {
      return "Please complete all required application fields.";
    }
    if (isSouthAfrican && !form.sa_id_number) {
      return "South African applicants must provide an ID number.";
    }
    if (!isSouthAfrican && !form.passport_number) {
      return "Applicants outside South Africa must provide a passport number.";
    }
    if (!form.consent_privacy || !form.consent_terms) {
      return "Please accept the privacy policy and terms before you continue.";
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitState.loading || submitState.success) {
      return;
    }
    const validationError = validate();
    if (validationError) {
      setSubmitState({ loading: false, error: validationError, success: null });
      return;
    }

    setSubmitState({ loading: true, error: "", success: null });
    try {
      const payload = {
        ...form,
        sa_id_number: isSouthAfrican ? form.sa_id_number || null : null,
        passport_number: isSouthAfrican ? null : form.passport_number || null,
      };
      const response = await api.post("/public/applications", payload);
      setSubmitState({ loading: false, error: "", success: response.data });
    } catch (error) {
      setSubmitState({ loading: false, error: getErrorMessage(error, "We could not submit your application."), success: null });
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-20">
      <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
        <AuthPanel
          eyebrow="Apply"
          title="Apply for the SLP"
          description="Complete the form below to create your applicant record, receive your student number, and prepare for secure password setup."
          testId="apply-intro"
        >
          <div className="space-y-4 text-sm leading-7 text-slate-700">
            <p data-testid="apply-intro-fee-text">Application fee: R{publicConfig?.application_fee || 150}</p>
            <p data-testid="apply-intro-consent-text">Your information will be used only for Theology SLP application processing, communication, payment matching, and student onboarding.</p>
            <p data-testid="apply-intro-support-text">Support: {publicConfig?.support_email}</p>
            <div className="rounded-[1.5rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid="apply-intro-checklist-card">
              <p className="text-sm font-semibold text-slate-900" data-testid="apply-intro-checklist-heading">What happens next</p>
              <ul className="mt-3 space-y-2 text-sm leading-7 text-slate-700">
                <li data-testid="apply-intro-checklist-item-1">Your unique student number is created securely for your application.</li>
                <li data-testid="apply-intro-checklist-item-2">A secure setup email is sent so you can create your password.</li>
                <li data-testid="apply-intro-checklist-item-3">After signing in, you can view payment guidance and track your application status.</li>
              </ul>
            </div>
          </div>
        </AuthPanel>

        {submitState.success ? (
          <div ref={successRef}>
            <AuthPanel
              eyebrow="Application received"
              title="Application received"
              description="Your application has been recorded successfully. Follow the next steps below to continue securely."
              testId="apply-success"
            >
              <StatusMessage message={submitState.error} type="error" testId="apply-error-message" />
              <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-5" data-testid="apply-success-card">
                <p className="text-3xl font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="apply-success-student-number">Save your student number: {submitState.success.student_number}</p>
                <div className="mt-4 space-y-2 text-sm leading-7 text-slate-700">
                  <p data-testid="apply-success-guidance-1">1. Save this student number and keep it safe.</p>
                  <p data-testid="apply-success-guidance-2">2. To process your application, please create a password and log in to your student profile.</p>
                  <p data-testid="apply-success-guidance-3">3. Once you have logged in, pay the R{publicConfig?.application_fee || 150} application fee and submit your proof of payment.</p>
                  <p data-testid="apply-success-guidance-4">4. You can then track your application status from your student profile.</p>
                </div>
                <p className="mt-4 rounded-[1.1rem] border border-[#f3d5a3] bg-[#fff8ef] px-4 py-3 text-base font-medium leading-7 text-slate-800" data-testid="apply-success-next-step-text">
                  {getFriendlyEmailNotice(submitState.success.email_delivery_status, submitState.success.support_email, "setup")}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button asChild className="rounded-full" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} data-testid="apply-success-student-login-button">
                    <Link to="/login">Student login</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-full border-[#d7c6a9]" data-testid="apply-success-contact-button">
                    <Link to="/contact">Contact support</Link>
                  </Button>
                </div>
              </div>
            </AuthPanel>
          </div>
        ) : (
          <AuthPanel
            eyebrow="Application form"
            title="Applicant details"
            description="South African applicants must provide an ID number. Other applicants must provide a passport number."
            testId="apply-form"
          >
            <form className="space-y-6" onSubmit={handleSubmit} data-testid="apply-form-fields">
              <StatusMessage message={submitState.error} type="error" testId="apply-error-message" />

              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-800" htmlFor="apply-first-name">First name</label>
                  <Input id="apply-first-name" data-testid="apply-first-name-input" value={form.first_name} onChange={(event) => updateField("first_name", event.target.value)} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-800" htmlFor="apply-surname">Surname</label>
                  <Input id="apply-surname" data-testid="apply-surname-input" value={form.surname} onChange={(event) => updateField("surname", event.target.value)} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-800" htmlFor="apply-nationality">Nationality</label>
                  <select id="apply-nationality" className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm" data-testid="apply-nationality-select" value={form.nationality} onChange={(event) => {
                    const nextNationality = event.target.value;
                    setForm((current) => ({
                      ...current,
                      nationality: nextNationality,
                      country: nextNationality === "South African" ? "South Africa" : current.country === "South Africa" ? "" : current.country,
                      sa_id_number: nextNationality === "South African" ? current.sa_id_number : "",
                      passport_number: nextNationality === "South African" ? "" : current.passport_number,
                    }));
                  }}>
                    <option>South African</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-800" htmlFor="apply-country">Country</label>
                  {isSouthAfrican ? (
                    <Input id="apply-country" data-testid="apply-country-input" value={form.country} readOnly disabled />
                  ) : (
                    <CountryCombobox value={form.country} onChange={(nextCountry) => updateField("country", nextCountry)} />
                  )}
                </div>
                {isSouthAfrican ? (
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-slate-800" htmlFor="apply-sa-id">South African ID number</label>
                    <Input id="apply-sa-id" data-testid="apply-sa-id-input" value={form.sa_id_number} onChange={(event) => updateField("sa_id_number", event.target.value)} />
                  </div>
                ) : (
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-slate-800" htmlFor="apply-passport">Passport number</label>
                    <Input id="apply-passport" data-testid="apply-passport-input" value={form.passport_number} onChange={(event) => updateField("passport_number", event.target.value)} />
                  </div>
                )}
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-slate-800" htmlFor="apply-email">Email address</label>
                  <Input id="apply-email" type="email" data-testid="apply-email-input" value={form.email} onChange={(event) => updateField("email", event.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-slate-800" htmlFor="apply-address">Physical address</label>
                  <Textarea id="apply-address" data-testid="apply-address-input" value={form.physical_address} onChange={(event) => updateField("physical_address", event.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-slate-800" htmlFor="apply-phone">Phone number</label>
                  <Input id="apply-phone" data-testid="apply-phone-input" value={form.phone_number} onChange={(event) => updateField("phone_number", event.target.value)} />
                </div>
              </div>

              <div className="space-y-4 rounded-[1.5rem] border border-stone-200 bg-[#fcfaf5] p-5">
                <div className="flex items-start gap-3">
                  <Checkbox id="apply-consent-privacy" checked={form.consent_privacy} onCheckedChange={(checked) => updateField("consent_privacy", Boolean(checked))} data-testid="apply-consent-privacy-checkbox" />
                  <label htmlFor="apply-consent-privacy" className="text-sm leading-7 text-slate-700" data-testid="apply-consent-privacy-text">
                    I consent to the use of my personal information for application processing, student onboarding, payment matching, and related academic administration in line with the privacy policy.
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="apply-consent-terms" checked={form.consent_terms} onCheckedChange={(checked) => updateField("consent_terms", Boolean(checked))} data-testid="apply-consent-terms-checkbox" />
                  <label htmlFor="apply-consent-terms" className="text-sm leading-7 text-slate-700" data-testid="apply-consent-terms-text">
                    I confirm that the information provided is accurate and I accept the terms and disclaimer for this portal.
                  </label>
                </div>
              </div>

              <Button type="submit" className="min-h-12 w-full rounded-full px-6 sm:w-auto" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} disabled={submitState.loading} data-testid="apply-submit-button">
                {submitState.loading ? "Submitting application..." : "Submit application"}
              </Button>
            </form>
          </AuthPanel>
        )}
      </div>
    </div>
  );
};


export const LoginPage = ({ publicConfig }) => {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const [loginForm, setLoginForm] = useState({ student_number: "", password: "" });
  const [resendForm, setResendForm] = useState({ student_number: "", email: "" });
  const [forgotForm, setForgotForm] = useState({ student_number: "", email: "" });
  const [loginState, setLoginState] = useState({ error: "", loading: false });
  const [helperState, setHelperState] = useState({ resend: "", forgot: "" });

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginState({ error: "", loading: true });
    try {
      await api.post("/auth/student/login", {
        student_number: loginForm.student_number.trim(),
        password: loginForm.password,
      });
      await refreshSession();
      navigate("/student");
    } catch (error) {
      setLoginState({ error: getErrorMessage(error, "Unable to sign in."), loading: false });
      return;
    }
    setLoginState({ error: "", loading: false });
  };

  const handleResend = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post("/auth/setup/resend", {
        student_number: resendForm.student_number.trim(),
        email: resendForm.email.trim(),
      });
      setHelperState((current) => ({
        ...current,
        resend: getFriendlyEmailNotice(response.data.email_delivery_status, publicConfig?.support_email, "setup"),
      }));
    } catch (error) {
      setHelperState((current) => ({ ...current, resend: getErrorMessage(error, "We could not resend the setup link.") }));
    }
  };

  const handleForgot = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post("/auth/password/forgot", {
        student_number: forgotForm.student_number.trim(),
        email: forgotForm.email.trim(),
      });
      setHelperState((current) => ({
        ...current,
        forgot: getFriendlyEmailNotice(response.data.email_delivery_status, publicConfig?.support_email, "reset"),
      }));
    } catch (error) {
      setHelperState((current) => ({ ...current, forgot: getErrorMessage(error, "We could not start the reset flow.") }));
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-20">
      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <AuthPanel
          eyebrow="Student login"
          title="Student login"
          description="After you complete your password setup, use your student number and password to view your application status, payment details, and module list."
          testId="student-login-intro"
        >
          <div className="space-y-3 text-sm leading-7 text-slate-700">
            <p data-testid="student-login-help-text">Only sign in after you have created your password from the secure setup email. If setup is not complete yet, use the resend setup link form below.</p>
            <p data-testid="student-login-support-text">Support: {publicConfig?.support_email}</p>
          </div>
        </AuthPanel>

        <AuthPanel
          eyebrow="Portal access"
          title="Student sign-in"
          description="Use your student number and password to sign in securely and view your application progress."
          testId="student-login-form"
        >
          <form className="space-y-4" onSubmit={handleLogin}>
            <StatusMessage message={loginState.error} type="error" testId="student-login-error-message" />
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-800" htmlFor="student-login-number">Student number</label>
              <Input id="student-login-number" data-testid="student-login-number-input" value={loginForm.student_number} onChange={(event) => setLoginForm((current) => ({ ...current, student_number: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-800" htmlFor="student-login-password">Password</label>
              <PasswordField id="student-login-password" data-testid="student-login-password-input" value={loginForm.password} onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))} />
            </div>
            <Button type="submit" className="min-h-12 w-full rounded-full px-6 sm:w-auto" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} disabled={loginState.loading} data-testid="student-login-submit-button">
              {loginState.loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-7 grid gap-5 lg:grid-cols-2">
            <div className="space-y-3 rounded-[1.5rem] border border-stone-200 bg-[#fcfaf5] p-5">
              <p className="text-[1rem] font-semibold tracking-[-0.015em] text-slate-900" data-testid="resend-setup-heading">Resend setup link</p>
              <form className="space-y-3" onSubmit={handleResend}>
                <Input placeholder="Student number" data-testid="resend-setup-student-number-input" value={resendForm.student_number} onChange={(event) => setResendForm((current) => ({ ...current, student_number: event.target.value }))} />
                <Input placeholder="Registered email" type="email" data-testid="resend-setup-email-input" value={resendForm.email} onChange={(event) => setResendForm((current) => ({ ...current, email: event.target.value }))} />
                <Button type="submit" variant="outline" className="min-h-11 w-full rounded-full border-[#d7c6a9] sm:w-auto" data-testid="resend-setup-submit-button">Send new setup link</Button>
              </form>
              <StatusMessage message={helperState.resend} type="success" testId="resend-setup-status-message" />
            </div>

            <div className="space-y-3 rounded-[1.5rem] border border-stone-200 bg-[#fcfaf5] p-5">
              <p className="text-[1rem] font-semibold tracking-[-0.015em] text-slate-900" data-testid="forgot-password-heading">Forgot password</p>
              <form className="space-y-3" onSubmit={handleForgot}>
                <Input placeholder="Student number" data-testid="forgot-password-student-number-input" value={forgotForm.student_number} onChange={(event) => setForgotForm((current) => ({ ...current, student_number: event.target.value }))} />
                <Input placeholder="Registered email" type="email" data-testid="forgot-password-email-input" value={forgotForm.email} onChange={(event) => setForgotForm((current) => ({ ...current, email: event.target.value }))} />
                <Button type="submit" variant="outline" className="min-h-11 w-full rounded-full border-[#d7c6a9] sm:w-auto" data-testid="forgot-password-submit-button">Request reset link</Button>
              </form>
              <StatusMessage message={helperState.forgot} type="success" testId="forgot-password-status-message" />
            </div>
          </div>
        </AuthPanel>
      </div>
    </div>
  );
};


const PasswordFormPage = ({ title, description, endpoint, successTestId }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState({ error: "", success: "", loading: false });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password.length < 10) {
      setStatus({ error: "Please use at least 10 characters for your password.", success: "", loading: false });
      return;
    }
    if (password !== confirmPassword) {
      setStatus({ error: "Passwords do not match.", success: "", loading: false });
      return;
    }

    setStatus({ error: "", success: "", loading: true });
    try {
      const token = searchParams.get("token")?.trim();
      if (!token) {
        setStatus({ error: "This setup link is invalid or missing. Please request a new setup link.", success: "", loading: false });
        return;
      }
      const response = await api.post(endpoint, { token, password });
      setStatus({ error: "", success: response.data.message, loading: false });
      const nextRoute = endpoint === "/auth/setup/complete" && response.data?.message?.toLowerCase().includes("admin") ? "/admin/login" : "/login";
      setTimeout(() => navigate(nextRoute), SUCCESS_REDIRECT_DELAY_MS);
    } catch (error) {
      setStatus({ error: getErrorMessage(error, "This link could not be completed."), success: "", loading: false });
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-20">
      <AuthPanel eyebrow="Secure access" title={title} description={description} testId={successTestId}>
        <form className="space-y-5" onSubmit={handleSubmit}>
          <StatusMessage message={status.error} type="error" testId={`${successTestId}-error-message`} />
          <StatusMessage message={status.success} type="success" testId={`${successTestId}-success-message`} />
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800" htmlFor={`${successTestId}-password`}>Password</label>
            <PasswordField id={`${successTestId}-password`} data-testid={`${successTestId}-password-input`} value={password} onChange={(event) => setPassword(event.target.value)} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800" htmlFor={`${successTestId}-confirm-password`}>Confirm password</label>
            <PasswordField id={`${successTestId}-confirm-password`} data-testid={`${successTestId}-confirm-password-input`} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
          </div>
          <Button type="submit" className="min-h-12 w-full rounded-full px-6 sm:w-auto" style={{ backgroundColor: "var(--brand-primary-orange, #F5A623)", color: "var(--brand-primary-dark, #2B2B2B)" }} disabled={status.loading} data-testid={`${successTestId}-submit-button`}>
            {status.loading ? "Saving..." : "Save password"}
          </Button>
        </form>
      </AuthPanel>
    </div>
  );
};


export const SetupPasswordPage = () => (
  <PasswordFormPage
    title="Create your portal password"
    description="Use the one-time setup link sent to your email to create a safe password for portal sign-in."
    endpoint="/auth/setup/complete"
    successTestId="setup-password"
  />
);


export const ResetPasswordPage = () => (
  <PasswordFormPage
    title="Reset your password"
    description="Use the reset link from your email to choose a new password for your student portal account."
    endpoint="/auth/password/reset"
    successTestId="reset-password"
  />
);


export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [status, setStatus] = useState({ error: "", loading: false });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ error: "", loading: true });
    try {
      await api.post("/auth/admin/login", { email: form.email.trim(), password: form.password });
      await refreshSession();
      navigate("/admin/dashboard");
    } catch (error) {
      setStatus({ error: getErrorMessage(error, "Unable to sign in to the admin area."), loading: false });
      return;
    }
    setStatus({ error: "", loading: false });
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8 lg:py-24">
      <AuthPanel
        eyebrow="Administration"
        title="Admin login"
        description="Assigned admin accounts can sign in here. Seeded accounts must change their password on first login before any administrative actions are allowed."
        testId="admin-login"
      >
        <div className="mb-6 rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5" data-testid="admin-login-note-card">
          <p className="text-sm leading-7 text-slate-700" data-testid="admin-login-note-text">
            Restricted administrative access only. Use your assigned admin email and password, or complete your first-time setup from the onboarding email before signing in.
          </p>
        </div>

        <form className="max-w-2xl space-y-4" onSubmit={handleSubmit}>
          <StatusMessage message={status.error} type="error" testId="admin-login-error-message" />
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800" htmlFor="admin-login-email">Admin email</label>
            <Input id="admin-login-email" type="email" data-testid="admin-login-email-input" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800" htmlFor="admin-login-password">Password</label>
            <PasswordField id="admin-login-password" data-testid="admin-login-password-input" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
          </div>
          <Button type="submit" className="min-h-12 w-full rounded-full px-6 sm:w-auto" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)", color: "var(--brand-white, #FFFFFF)" }} disabled={status.loading} data-testid="admin-login-submit-button">
            {status.loading ? "Signing in..." : "Sign in to admin portal"}
          </Button>
        </form>
      </AuthPanel>
    </div>
  );
};